<template>
  <div class="flex flex-col gap-4">
    <!-- Firmware Selection Dropdown -->
    <div v-if="canSelectFirmware">
      <FirmwareSelectionDropdown v-model="selectedFirmwareId" @firmware-selected="onFirmwareSelected" />
      <div class="mt-1">
        <button
          type="button"
          @click="switchToUrlMode"
          class="text-xs text-primary-600 hover:text-primary-800 underline"
        >
          {{ t('cpms.firmware.selector.useCustomUrl') }}
        </button>
      </div>
    </div>

    <!-- Custom URL Input -->
    <div v-else>
      <InfuseField :label="t('cpms.firmware.url.label')" size="small">
        <InfuseInput
          v-model="customFirmwareUrl"
          :placeholder="t('cpms.firmware.url.placeholder')"
          class="w-full"
          size="small"
        />
      </InfuseField>
      <div v-if="firmwareAvailable" class="mt-1">
        <button
          type="button"
          @click="switchToSelectMode"
          class="text-xs text-primary-600 hover:text-primary-800 underline"
        >
          {{ t('cpms.firmware.selector.selectFirmware') }}
        </button>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { Firmware } from '@infuse/api/cpms/firmware-management';
import FirmwareSelectionDropdown from './FirmwareSelectionDropdown.vue';
import { InfuseField, InfuseInput } from '@infuse/ui/components';
import { useI18n } from 'vue-i18n';
import { computed, ref, watch } from 'vue';
import { Feature, useFeatureStore } from '@infuse/cpms/shared/features/store';
import { firmwareManagementPermissions } from '@infuse/api/cpms/firmware-management';
import { useCurrentUserStore } from '@infuse/iam/user/store';

const { t } = useI18n();
const featureStore = useFeatureStore();
const currentUserStore = useCurrentUserStore();

// Check if user has permission to search firmware (or IAM is disabled)
const hasFirmwareSearchPermission = computed(
  () => !currentUserStore.enabled || currentUserStore.hasPermission(firmwareManagementPermissions.SEARCH)
);

// Firmware data is loaded by FirmwareSelectionDropdown when rendered

interface Props {
  modelValue?: {
    firmware?: Firmware;
    customFirmwareUrl?: string;
    selectionMode?: 'select' | 'url';
  };
}

const props = withDefaults(defineProps<Props>(), {
  modelValue: () => ({})
});

interface Emits {
  (
    e: 'update:modelValue',
    value: {
      firmware?: Firmware;
      customFirmwareUrl?: string;
      selectionMode: 'select' | 'url';
    }
  ): void;
  (e: 'firmware-selected', firmware?: Firmware): void;
  (e: 'url-changed', url: string): void;
}

const emit = defineEmits<Emits>();

const firmwareAvailable = computed(
  () => featureStore.hasFeature(Feature.FIRMWARE_MANAGEMENT) && hasFirmwareSearchPermission.value
);

// 'select' is only a real mode when firmware can be listed; otherwise the URL input is
// what renders, so the mode has to say 'url' or every emit would misdescribe the state.
const normalizeMode = (mode?: 'select' | 'url'): 'select' | 'url' => {
  if (!firmwareAvailable.value) return 'url';
  return mode ?? 'select';
};

const selectionMode = ref<'select' | 'url'>(normalizeMode(props.modelValue?.selectionMode));
const selectedFirmwareId = ref<string | undefined>(props.modelValue?.firmware?.id);
const selectedFirmware = ref<Firmware | undefined>(props.modelValue?.firmware);
const customFirmwareUrl = ref<string>(props.modelValue?.customFirmwareUrl || '');

const canSelectFirmware = computed(() => selectionMode.value === 'select' && firmwareAvailable.value);

// Watch for prop changes
watch(
  () => props.modelValue,
  (newValue) => {
    if (newValue?.selectionMode) {
      selectionMode.value = normalizeMode(newValue.selectionMode);

      if (selectionMode.value === 'url') {
        selectedFirmware.value = undefined;
        selectedFirmwareId.value = undefined;
      } else {
        customFirmwareUrl.value = '';
      }
    }
    if (newValue?.firmware) {
      selectedFirmware.value = newValue.firmware;
      selectedFirmwareId.value = newValue.firmware.id;
    } else {
      selectedFirmware.value = undefined;
      selectedFirmwareId.value = undefined;
    }
    if (newValue?.customFirmwareUrl !== undefined) {
      customFirmwareUrl.value = newValue.customFirmwareUrl;
    }
  },
  { immediate: true }
);

// Watch for firmware availability changes and auto-switch to URL mode if needed
watch(firmwareAvailable, (available) => {
  if (!available && selectionMode.value === 'select') {
    selectionMode.value = 'url';
  }
});

// Also watch for permission changes
watch(hasFirmwareSearchPermission, (hasPermission) => {
  if (!hasPermission && selectionMode.value === 'select') {
    selectionMode.value = 'url';
  }
});

// Watch for internal changes and emit updates
watch([selectionMode, selectedFirmware, customFirmwareUrl], () => {
  const value = {
    selectionMode: selectionMode.value,
    firmware: selectedFirmware.value,
    customFirmwareUrl: customFirmwareUrl.value
  };
  emit('update:modelValue', value);
});

watch(customFirmwareUrl, (newUrl) => {
  emit('url-changed', newUrl);
});

const switchToUrlMode = () => {
  selectionMode.value = 'url';
  selectedFirmware.value = undefined;
  selectedFirmwareId.value = undefined;
};

const switchToSelectMode = () => {
  selectionMode.value = 'select';
  customFirmwareUrl.value = '';
};

const onFirmwareSelected = (firmware?: Firmware) => {
  selectedFirmware.value = firmware;
  // Clear custom URL when firmware is selected
  if (firmware) {
    customFirmwareUrl.value = '';
  }
  emit('firmware-selected', firmware);
};
</script>
